import React, { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { Button } from "../../../../components/ui/button";
import { Spinner } from "../../../../components/ui/spinner";
import { cn } from "../../../../lib/utils";
import type { EquipmentListProps } from "../../../../types/baseTypes";
import { setNextButtonClick } from "../../../../redux/slice/temporaryStorage/temporaryStorageSlice";
import StorageListAccordion from "../../common/components/LocationSelectionStep/StorageListAccordion";
import ListLoadError from "../../common/components/ListLoadError";
import { useTemporaryStorageStepCounter } from "../hooks/useTemporaryStorageStepCounter";
import {
  assetGroupActivated,
  assetGroupDeactivated,
  setSelectedGroup,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";

/**
 * TemporaryStorageLockerSizeSelectionStep
 * ---------------------------------------------------------------
 * Step component for selecting locker size during temporary storage flow.
 * 
 * @remarks
 * - Displays available storage groups/locker sizes
 * - Handles loading and empty states with proper accessibility
 * - Accordion interaction with selection state
 * - Fixed bottom action button with pulse effect on selection
 */
function TemporaryStorageLockerSizeSelectionStep() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { current, total } = useTemporaryStorageStepCounter("lockerSizeSelection");

  const { selectedGroup, selectedLocation } = useSelector(
    (state: RootState) => state.bookingCreation
  );

  const groupsByLocation = useSelector(
    (state: RootState) => state.bookingCreation.groupsByLocation
  );

  const storages: EquipmentListProps[] = useMemo(
    () =>
      selectedLocation && groupsByLocation[selectedLocation.id]
        ? groupsByLocation[selectedLocation.id]
        : [],
    [selectedLocation, groupsByLocation]
  );

  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(
    selectedGroup ? selectedGroup.id : null
  );
  const [pulsing, setPulsing] = React.useState(false);

  // Memoize derived state to prevent unnecessary recalculations
  // `isLoading` below is the list being empty, so a failed load would spin
  // forever without this (#1970).
  const loadFailed = useSelector(
    (state: RootState) =>
      !!selectedLocation &&
      state.bookingCreation.groupsLoadFailedLocationId === selectedLocation.id
  );

  const { isLoading, hasStorages, validStorages } = useMemo(() => {
    const loading = storages.length === 0;
    const valid = storages.filter((storage) => storage.priceCategory);
    
    return {
      isLoading: loading,
      hasStorages: valid.length > 0,
      validStorages: valid,
    };
  }, [storages]);

  // Bounce effect when storage is selected
  React.useEffect(() => {
    if (selectedGroup?.id == null) return;
    setPulsing(true);
    const tmr = setTimeout(() => setPulsing(false), 250);
    return () => clearTimeout(tmr);
  }, [selectedGroup?.id]);

  const handleAccordionChange = (storage: EquipmentListProps) => {
    setExpandedAccordion(expandedAccordion === storage.id ? null : storage.id);
    if (expandedAccordion === storage.id) {
      dispatch(assetGroupDeactivated());
    } else {
      dispatch(assetGroupActivated(storage));
      dispatch(setSelectedGroup(storage));
    }
  };

  const handleClickNext = () => {
    dispatch(setNextButtonClick());
  };

  return (
    <div
      className="flex flex-col justify-center mx-5"
      role="region"
      aria-label={t("temporary_storage.new_booking.locker_size.title")}
      aria-busy={isLoading && !loadFailed}
    >
      {/* Step Counter */}
      <p className="-mt-5 mb-6 text-xs text-muted-foreground text-center" role="status" aria-live="polite">
        {t("temporary_storage.new_booking.step_counter", {
          current,
          total,
          defaultValue: "Schritt {{current}} von {{total}}",
        })}
      </p>

      {loadFailed ? (
        <ListLoadError messageKey="book_location.error.fetch_equipment" />
      ) : isLoading ? (
        <div className="flex w-full items-center justify-center py-6" aria-live="polite">
          <Spinner size="md" />
        </div>
      ) : hasStorages ? (
        <div className="space-y-2 animate-in fade-in slide-in-from-top-4 duration-500">
          {validStorages.map((item) => (
            <StorageListAccordion
              key={item.id}
              assetGroup={item}
              active={expandedAccordion === item.id}
              onToggle={() => handleAccordionChange(item)}
            />
          ))}
        </div>
      ) : (
        <p className="text-slate-700" aria-live="polite">
          {t("temporary_storage.new_booking.locker_size.no_storage")}
        </p>
      )}

      <div className="fixed flex w-full max-w-md px-5 left-0 items-center justify-center right-0 bottom-4 m-auto">
        <Button
          size="lg"
          variant="primary"
          className={cn(
            "w-full transition-all duration-300 ease-out will-change-transform",
            pulsing && "scale-[1.03]"
          )}
          onClick={handleClickNext}
          disabled={!selectedGroup}
        >
          {t("temporary_storage.new_booking.location.next_button_title")}
        </Button>
      </div>
    </div>
  );
}

export default memo(TemporaryStorageLockerSizeSelectionStep);
